import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import axiosInstance from '../../../services/AxiosInstance';
import PageTitle from '../../../jsx/layouts/PageTitle';
import notify from '../../../utils/notification';

const STATUSES = [
  { value: '', label: 'All results' },
  { value: 'active', label: 'Allowed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'not_approved', label: 'Not approved' },
  { value: 'no_permission', label: 'No permission for place' },
  { value: 'outside_area', label: 'Outside the area (refused)' },
  { value: 'not_found', label: 'Not found / unreadable' },
];
const LABEL = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));
const EMPTY = { guard: null, status: '', from: '', to: '' };

// Every card scan made through the scanner PWA: who scanned, whom, when, and what the gate saw.
const SecurityScanLogs = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [guards, setGuards] = useState([]);
  const [list, setList] = useState({ rows: [], page: 1, totalPages: 1, total: 0, loading: true });

  useEffect(() => {
    axiosInstance
      .get('/security', { params: { limit: 100 } })
      .then(({ data }) => setGuards(data.data.map((g) => ({ value: g.id, label: `${g.name} (${g.email})` }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async (page, f) => {
    setList((l) => ({ ...l, loading: true }));
    try {
      const { data } = await axiosInstance.get('/security/scan-logs', {
        params: { page, limit: 20, security_id: f.guard || undefined, status: f.status || undefined, from: f.from || undefined, to: f.to || undefined },
      });
      setList({ rows: data.data, page, totalPages: data.totalPages, total: data.totalItems, loading: false });
    } catch (e) {
      setList((l) => ({ ...l, loading: false }));
      notify('error', e.response?.data?.message || e.message);
    }
  }, []);

  useEffect(() => {
    load(1, filters);
  }, [filters, load]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <>
      <PageTitle motherMenu="Security Scan Logs" activeMenu="Security Scan Logs" />

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h4 className="card-title mb-0">Scans by security staff ({list.total})</h4>
          <button className="btn btn-outline-primary btn-sm" onClick={() => load(list.page, filters)}>
            Refresh
          </button>
        </div>

        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label small text-muted mb-1">Security person</label>
              <Select
                isClearable
                options={guards}
                value={guards.find((g) => g.value === filters.guard) || null}
                onChange={(o) => set({ guard: o?.value ?? null })}
                placeholder="All"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">Result</label>
              <select className="form-select" value={filters.status} onChange={(e) => set({ status: e.target.value })}>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">From</label>
              <input type="date" className="form-control" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">To</label>
              <input type="date" className="form-control" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-outline-secondary w-100" onClick={() => setFilters(EMPTY)}>
                Reset
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Time</th><th>Scanned by</th><th>Person scanned</th><th>User type</th><th>Place</th><th>In / Out</th><th>Location</th><th>Result</th><th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="text-nowrap">{new Date(r.scanned_at).toLocaleString()}</td>
                    <td>
                      {r.guard}
                      {r.guard_email && <div className="small text-muted">{r.guard_email}</div>}
                    </td>
                    <td>
                      {r.person || <span className="text-muted">Unknown</span>}
                      {r.registration_data_id && <div className="small text-muted">#{r.registration_data_id}</div>}
                    </td>
                    <td>{r.user_type || '—'}</td>
                    <td>{r.place || '—'}</td>
                    <td>{r.direction ? <span className={`badge ${r.direction === 'in' ? 'bg-success' : 'bg-primary'}`}>{r.direction.toUpperCase()}</span> : '—'}{r.voided ? <span className="small text-muted"> undone</span> : null}</td>
                    <td>{r.geo_status ? <span className={`badge ${r.geo_status === 'inside' ? 'bg-success' : r.geo_status === 'outside' ? 'bg-danger' : 'bg-secondary'}`}>{r.geo_status === 'inside' ? 'Inside' : r.geo_status === 'outside' ? `${r.geo_distance} m away` : 'Unknown'}</span> : '—'}</td>
                    <td>
                      <span className={`badge ${r.allowed ? 'bg-success' : 'bg-danger'}`}>{LABEL[r.status] || r.status}</span>
                    </td>
                    <td>{r.reason || '—'}</td>
                  </tr>
                ))}
                {!list.loading && !list.rows.length && (
                  <tr><td colSpan={9} className="text-center text-muted">No scans found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {list.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center">
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page <= 1 || list.loading} onClick={() => load(list.page - 1, filters)}>Previous</button>
              <span className="small text-muted">Page {list.page} of {list.totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page >= list.totalPages || list.loading} onClick={() => load(list.page + 1, filters)}>Next</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SecurityScanLogs;
